import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import AddFormModal from '../AddFormModal/AddFormModal';
import './PrintModal.css';

const MANDATORY_COLUMNS = ['name', 'patient name'];

const isMandatoryColumn = (col) =>
  MANDATORY_COLUMNS.includes(String(col).toLowerCase().trim());

const PrintModal = ({
  isOpen,
  onClose,
  onPrint,
  availableColumns = [],
  title = 'Print Setup'
}) => {
  const [selectedColumns, setSelectedColumns] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedColumns(Array.isArray(availableColumns) ? [...availableColumns] : []);
  }, [isOpen, availableColumns]);

  const handleColumnToggle = (col) => {
    if (isMandatoryColumn(col)) return;
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  const toggleAllColumns = () => {
    if (!Array.isArray(availableColumns)) return;
    const nonMandatory = availableColumns.filter((c) => !isMandatoryColumn(c));
    const nonMandatorySelected = selectedColumns.filter((c) => !isMandatoryColumn(c));
    if (nonMandatorySelected.length === nonMandatory.length) {
      setSelectedColumns(availableColumns.filter(isMandatoryColumn));
    } else {
      setSelectedColumns([...availableColumns]);
    }
  };

  const handleConfirmPrint = () => {
    if (typeof onPrint === 'function') {
      onPrint(selectedColumns);
    }
  };

  return (
    <AddFormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={handleConfirmPrint}
      className="print-modal-overlay"
      footer={
        <div className="print-modal-footer">
          <button
            type="button"
            className="print-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="print-btn-confirm"
            onClick={handleConfirmPrint}
            disabled={selectedColumns.length === 0}
          >
            <FontAwesomeIcon icon={faPrint} /> Print
          </button>
        </div>
      }
    >
      <div className="print-modal-body">
        <p className="print-modal-subtitle">
          Select the fields/columns you would like to include in the printed report.
        </p>

        <div className="print-col-section">
          <div className="print-col-header">
            <span className="print-col-title">
              Fields ({selectedColumns.length} of {availableColumns.length} selected)
            </span>
            <button
              type="button"
              className="print-col-toggle-all"
              onClick={toggleAllColumns}
            >
              {selectedColumns.filter((c) => !isMandatoryColumn(c)).length ===
              availableColumns.filter((c) => !isMandatoryColumn(c)).length
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>

          <div className="print-col-grid">
            {availableColumns.map((col) => {
              const mandatory = isMandatoryColumn(col);
              const checked = selectedColumns.includes(col);
              return (
                <label
                  key={col}
                  className={`print-col-option${mandatory ? ' print-col-option--locked' : ''}`}
                  title={mandatory ? 'This column is required and cannot be removed' : ''}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleColumnToggle(col)}
                    disabled={mandatory}
                  />
                  <span>{col}</span>
                  {mandatory && <span className="print-col-lock-badge">Required</span>}
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </AddFormModal>
  );
};

export default PrintModal;
